/**
 * navFeatures.js
 *
 * Shared reference list of every plan-gateable nav feature in the app,
 * mirroring DashboardLayout.js's ALL_NAV paths and section groupings.
 *
 * Used ONLY by ManagePlans.js, to render checkboxes for "which features
 * does this plan include?" without hand-duplicating the full nav array
 * (with its icons, role lists, etc — none of which matter for this
 * purpose). DashboardLayout.js does NOT need to import this file — it
 * only checks `includedFeatures.includes(item.path)` against whatever
 * the backend returns, using its own existing `item.path` values.
 *
 * If you add a new page to DashboardLayout.js's ALL_NAV later, add the
 * same { path, name } pair here too so it shows up as a checkbox option
 * in the plan editor. This list is intentionally a SEPARATE, lightweight
 * reference — not a single source of truth both files must import from
 * — to avoid disrupting ALL_NAV's existing structure.
 */
const NAV_FEATURES = [
    // ── Common ──────────────────────────────────────────────────────────
    { section: 'Common', path: '/dashboard',  name: 'Dashboard' },
    { section: 'Common', path: '/my-profile', name: 'ESS Dashboard' },
    { section: 'Common', path: '/org-chart',  name: 'Org Chart' },
    { section: 'Common', path: '/announcements', name: 'Announcements' },
    // ── People & Org ────────────────────────────────────────────────────
    { section: 'People & Org', path: '/employees',   name: 'Employees' },
    { section: 'People & Org', path: '/departments', name: 'Departments' },
    { section: 'People & Org', path: '/locations',   name: 'Locations' },
    { section: 'People & Org', path: '/shifts',          name: 'Shifts' },
    { section: 'People & Org', path: '/shifts/roster',   name: 'Shift Roster' },
    { section: 'People & Org', path: '/loans',           name: 'Loans & Advances' },
    { section: 'People & Org', path: '/probation',       name: 'Probation Tracking' },
    // ── Recruitment ─────────────────────────────────────────────────────
    { section: 'Recruitment', path: '/ats',            name: 'ATS / Jobs' },
    { section: 'Recruitment', path: '/my-interviews',  name: 'My Interviews' },
    // ── Onboarding & Offboarding ────────────────────────────────────────
    { section: 'Onboarding & Offboarding', path: '/onboarding',  name: 'Onboarding' },
    { section: 'Onboarding & Offboarding', path: '/offboarding', name: 'Offboarding' },
    { section: 'Onboarding & Offboarding', path: '/offboarding/exit-interviews', name: 'Exit Interviews' },
    // ── Payroll ─────────────────────────────────────────────────────────
    { section: 'Payroll', path: '/payroll/management',  name: 'Payroll Management' },
    { section: 'Payroll', path: '/payroll/arrears',     name: 'Salary Arrears' },
    { section: 'Payroll', path: '/payroll/tax-summary', name: 'Tax Reports' },
    { section: 'Payroll', path: '/billing',             name: 'Billing & Plans' },
    { section: 'Payroll', path: '/payroll/my-payslips',            name: 'My Payslips' },
    { section: 'Payroll', path: '/payroll/investment-declaration', name: 'My Investment' },
    // ── Time & Attendance ───────────────────────────────────────────────
    { section: 'Time & Attendance', path: '/attendance',             name: 'Attendance' },
    { section: 'Time & Attendance', path: '/attendance/regularize',  name: 'Regularization' },
    { section: 'Time & Attendance', path: '/leave',                  name: 'Leave' },
    { section: 'Time & Attendance', path: '/timesheet',              name: 'Timesheet' },
    { section: 'Time & Attendance', path: '/holidays',               name: 'Holiday Calendar' },
    { section: 'Time & Attendance', path: '/remote-work',            name: 'Remote Work' },
    // ── Expenses ────────────────────────────────────────────────────────
    { section: 'Expenses', path: '/expenses', name: 'Expenses' },
    // ── Performance & Development ──────────────────────────────────────
    { section: 'Performance & Development', path: '/performance', name: 'Performance' },
    { section: 'Performance & Development', path: '/training',    name: 'Training & Learning' },
    { section: 'Performance & Development', path: '/skills',      name: 'Skills Matrix' },
    // ── Engagement & Compliance ─────────────────────────────────────────
    { section: 'Engagement & Compliance', path: '/surveys',             name: 'Surveys & Engagement' },
    { section: 'Engagement & Compliance', path: '/compliance-calendar', name: 'Compliance Calendar' },
    { section: 'Engagement & Compliance', path: '/documents',           name: 'Documents' },
    { section: 'Engagement & Compliance', path: '/helpdesk',            name: 'HR Help Desk' },
    // ── Analytics & Tools ───────────────────────────────────────────────
    { section: 'Analytics & Tools', path: '/reports',      name: 'Reports & Analytics' },
    { section: 'Analytics & Tools', path: '/audit-logs',   name: 'Audit Logs' },
    { section: 'Analytics & Tools', path: '/workflows',    name: 'Workflow Automation' },
    { section: 'Analytics & Tools', path: '/ai-assistant', name: 'AI Assistant' },
    // ── Manager ─────────────────────────────────────────────────────────
    { section: 'Manager', path: '/my-team', name: 'My Team' },
];

export default NAV_FEATURES;
